import React from 'react';
import { useSelector } from 'react-redux';
import { resolveMenuItems } from '@milanote/roma';

import { BrowserEngineType } from '../../../common/platform/platformTypes';
import { getClipboardElements } from '../shortcuts/clipboard/clipboardSelectors';
import { getSelectedElements } from '../../element/selection/selectedElementsSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { isGlobalDebugEnabled } from '../../debug/debugUtil';
import { useTranslator } from '../../components/i18n/useTranslator';
import { elementMobileMenuConfig } from './configs/elementContextMenuConfig';
import type { ElementContextMenuContext, MobileElementContextMenuActions } from './context/elementContextMenuContext';
import { deriveMenuContext } from './context/deriveMenuContext';
import { useElementContextMenuActions } from './useElementContextMenuActions';
import platformSingleton from '../../platform/platformSingleton';
import MobileRomaMenu from './MobileRomaMenu';

type MobileElementContextMenuV2Props = {
    isEditable?: boolean;
    showIcons?: boolean;
};

/** Checks whether the browser clipboard APIs needed by the copy-link action are available. */
const getClipboardInsertSupported = (): boolean =>
    !!navigator.permissions && !!navigator.clipboard && platformSingleton.browserEngine !== BrowserEngineType.firefox;

/** Renders Element toolbar More actions from the shared V2 context-menu registry. */
const MobileElementContextMenuV2: React.FC<MobileElementContextMenuV2Props> = ({ isEditable, showIcons = false }) => {
    const currentBoardId = useSelector(getCurrentBoardId);
    const selectedElements = useSelector(getSelectedElements);
    const clipboardElements = useSelector(getClipboardElements);
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const translate = useTranslator('canvas', 'contextMenuItems') as (localeKey: string) => string;
    const actions = useElementContextMenuActions({
        isEditable: !!isEditable,
    });

    const derivedContext = deriveMenuContext(
        { kind: 'element', currentBoardId, selectedElements },
        { elements: clipboardElements },
        { canWrite: !!isEditable, showDevTools: isGlobalDebugEnabled() },
        platformDetails,
    );
    const context: ElementContextMenuContext<MobileElementContextMenuActions> = {
        ...derivedContext,
        translate,
        actions,
        clipboardInsertSupported: getClipboardInsertSupported(),
        isMobile: true,
    };
    const nodes = resolveMenuItems(elementMobileMenuConfig, context);

    return <MobileRomaMenu ariaLabel="Element actions" nodes={nodes} showIcons={showIcons} />;
};

export default MobileElementContextMenuV2;
